import { apiGet, apiPost, apiPatch } from '../../../components/qv4/models/shared/apiClient';
export const catalogueCode = error => error?.response?.data?.code || error?.code;
export const catalogueFailure = error => error?.response?.data?.message || error?.message || 'Catalogue unavailable. Please retry.';
const unwrap = response => {
  if (!response.data?.success) throw Object.assign(new Error(response.data?.message || 'Catalogue request failed'), { code: response.data?.code, data: response.data?.data });
  return response.data.data;
};
export const mappingRoot = kind => kind === 'part' ? '/parts-v1/legacy-mappings' : '/vehicle-catalogue-v1/legacy-model-mappings';
export function listMappings(kind, filters = {}) {
  const params = new URLSearchParams();
  ['status', 'method', 'search', 'page', 'limit', 'flag', 'make_id', 'family_id', 'canonical_part_id'].forEach(key => { if (filters[key] != null && filters[key] !== '') params.set(key, String(filters[key])); });
  if (kind === 'vehicle_family') params.set('sort', filters.sort || 'usage_desc');
  return apiGet(`${mappingRoot(kind)}?${params}`).then(unwrap);
}
export const mappingSummary = kind => apiGet(`${mappingRoot(kind)}/summary`).then(unwrap);
export const mappingDetail = (kind, id) => apiGet(`${mappingRoot(kind)}/${id}`).then(unwrap);
export const reviewMapping = (kind, id, body) => apiPatch(`${mappingRoot(kind)}/${id}`, body).then(unwrap);
export function vehicleLinkBody(row, familyId, values = {}) {
  if (!Number.isSafeInteger(row.version) || row.version < 1) throw new Error('Reload the saved link before continuing.');
  const role = values.link_role || 'alias';
  if (!['alias', 'variant', 'ignore'].includes(role)) throw new Error('Choose how to link this name.');
  const body = { expected_version: row.version, link_role: role };
  if (role !== 'ignore') {
    if (!Number.isSafeInteger(Number(familyId)) || Number(familyId) < 1) throw new Error('Choose a model.');
    body.family_id = Number(familyId);
  }
  if (role === 'variant') {
    if (values.variant_id && values.variant_name?.trim()) throw new Error('Choose an existing variant or a new name, not both.');
    if (values.variant_id) {
      if (!Number.isSafeInteger(Number(values.variant_id)) || Number(values.variant_id) < 1) throw new Error('Choose a valid variant.');
      body.variant_id = Number(values.variant_id);
    }
    else if (values.variant_name?.trim()) body.variant_name = values.variant_name.trim();
    else throw new Error('Choose a variant or enter its new name.');
  }
  if (values.correctMake) {
    if (!values.review_notes?.trim()) throw new Error('Explain why the make needs correcting.');
    body.confirm_make_correction = true;
  }
  if (values.review_notes?.trim()) body.review_notes = values.review_notes.trim();
  return body;
}
export const bulkReview = (kind, items) => apiPost(`${mappingRoot(kind)}/bulk`, { items }).then(unwrap);
export function mappingDecision(kind, row, status, target = null, note = null) {
  if (!Number.isInteger(row.version) || row.version < 1) throw new Error('Reload the current mapping before reviewing.');
  if (status === 'mapped' && (!Number.isInteger(Number(target)) || Number(target) < 1)) throw new Error('Pick an active catalogue entry.');
  return { expected_version: row.version, mapping_status: status, [kind === 'part' ? 'canonical_part_id' : 'family_id']: status === 'mapped' ? Number(target) : null, review_notes: note };
}
export const getRequest = id => apiGet(`/catalogue-requests/${id}`).then(unwrap);
function targetKind(kind) {
  if (!['part', 'vehicle_family', 'vehicle_variant'].includes(kind)) throw new Error('A valid catalogue target kind is required.');
}
export function listTargets(kind, { search = '', page = 1, limit = 50, makeId, familyId } = {}) {
  targetKind(kind);
  const params = new URLSearchParams({ kind, status: 'active', search, page: String(page), limit: String(limit) });
  if (kind === 'vehicle_family' && makeId != null) params.set('make_id', String(makeId));
  if (kind === 'vehicle_variant' && familyId != null) params.set('family_id', String(familyId));
  return apiGet(`/catalogue-requests/targets?${params}`).then(unwrap);
}
export function getTarget(kind, id, makeId, familyId) {
  targetKind(kind);
  if (!Number.isSafeInteger(Number(id)) || Number(id) < 1) throw new Error('Pick a valid catalogue entry.');
  const params = new URLSearchParams();
  if (kind === 'vehicle_family' && makeId != null) params.set('make_id', String(makeId));
  if (kind === 'vehicle_variant' && familyId != null) params.set('family_id', String(familyId));
  const query = params.toString();
  return apiGet(`/catalogue-requests/targets/${kind}/${Number(id)}${query ? `?${query}` : ''}`).then(unwrap);
}
export const listRequests = (filters = {}) => apiGet(`/catalogue-requests?${new URLSearchParams(Object.entries(filters).filter(([, value]) => value != null && value !== ''))}`).then(unwrap);
export const catalogueRequestsChanged = () => window.dispatchEvent(new Event('catalogue-requests-changed'));
export const createRequest = body => apiPost('/catalogue-requests', body).then(unwrap).then(data => { catalogueRequestsChanged(); return data; });
export const resolveRequest = (id, body) => apiPost(`/catalogue-requests/${id}/resolve`, body).then(unwrap).then(data => { catalogueRequestsChanged(); return data; });
export function requestBody(kind, text, anchors = {}) {
  const body = { kind, typed_text: text.trim(), expected_version: 0 };
  if (['vehicle_family', 'vehicle_variant'].includes(kind)) body.make_id = Number(anchors.make_id);
  if (kind === 'vehicle_variant') body.family_id = Number(anchors.family_id);
  if (Number.isInteger(Number(anchors.query_pk)) && Number(anchors.query_pk) > 0) body.query_pk = Number(anchors.query_pk);
  if (kind === 'part' && Number.isInteger(Number(anchors.query_part_id)) && Number(anchors.query_part_id) > 0) body.query_part_id = Number(anchors.query_part_id);
  return body;
}
export function resolutionBody(request, values, target) {
  const base = { action: values.action, expected_version: request.version };
  if (values.action === 'reject') return { ...base, reason: values.reason.trim() };
  const mapping_versions = {};
  (request.sources || []).forEach(source => {
    if (source.legacy_row_id == null) return;
    const mapping = (request.legacy_mappings || []).find(row => Number(row.legacy_row_id) === Number(source.legacy_row_id));
    mapping_versions[source.legacy_row_id] = mapping?.version ?? 0;
  });
  if (values.action === 'link') {
    if (!target?.version || target.status !== 'active' || (request.kind === 'vehicle_family' && Number(target.make_id) !== Number(request.make_id)) || (request.kind === 'vehicle_variant' && Number(target.family_id) !== Number(request.family_id))) throw new Error('Load an active target belonging to this request.');
    return { ...base, mapping_versions, canonical_id: target.id, canonical_expected_version: target.version, add_alias: values.add_alias === true };
  }
  return { ...base, mapping_versions, canonical_name: values.canonical_name.trim(), add_alias: false, confirm_not_duplicate: values.confirm_not_duplicate === true };
}
export const accuracyReasons = ['low_name_similarity', 'side_conflict', 'method_usage_mismatch', 'basemodel_mismatch', 'canonical_no_category', 'single_part_category'];
export const reasonLabel = value => ({ low_name_similarity: 'Names differ', side_conflict: 'Side conflict', method_usage_mismatch: 'Usage differs', basemodel_mismatch: 'Base model differs', canonical_no_category: 'No active category', single_part_category: 'Only part in category' })[value] || value;
export const hasExactName = (rows, text) => {
  const normalize = value => String(value || '').normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  return rows.some(row => normalize(row.canonical_name || row.label || row.name) === normalize(text) || (row.aliases || []).some(alias => normalize(alias.alias || alias.label || alias) === normalize(text)));
};
